/**
 * Who holds the keys to this calendar.
 *
 * A calendar starts anonymous: the owner link *is* the account. Claiming it
 * attaches it to a passkey or a GitHub identity, after which the link can be
 * replaced or switched off.
 */
import type { Calendar, User } from '@circle/shared';
import { api } from '../app/api';
import { ownerTokens } from '../app/owner-tokens';
import { ownerLinkActions, ownerLinkRow } from './owner-link';
import {
  browserSupportsWebAuthn,
  githubEnabled,
  githubSignInUrl,
  loadAppConfig,
  passkeyErrorMessage,
  rememberGithubSignIn,
  registerPasskey,
  signInWithPasskey,
} from '../app/auth';
import { button, el, textField, withBusy, type Child } from './dom';
import { openDialog } from './dialog';
import { toast, toastError } from './toast';
import type { Panel } from './panel';

export interface SecurityPanelOptions {
  panel: Panel;
  calendar: Calendar;
  user: User | null;
  onUpdated: (calendar: Calendar) => void;
  onSignedIn: (user: User) => void;
  /**
   * The calendar has been deleted; the page it was showing has to go
   * somewhere else. Leave it out and the panel offers no way to delete —
   * this is the whole of "owner only", since only an owner's page mounts it.
   */
  onDeleted?: () => void;
}

export function openSecurityPanel(options: SecurityPanelOptions): void {
  const { panel } = options;
  if (panel.openKey === 'security') {
    panel.close();
    return;
  }

  let calendar = options.calendar;
  let user = options.user;
  let message: { text: string; kind: 'info' | 'alert' } | undefined;
  /** Survives the repaints, so a half-typed name is not lost to a re-render. */
  let displayName = user?.name ?? '';

  const repaint = (): void => {
    panel.setBody(content());
  };

  // The panel opens on a click, so it may be the first thing to need the
  // deployment's provider list. Paint now, correct the GitHub row when it lands.
  void loadAppConfig().then(() => {
    if (panel.openKey === 'security') repaint();
  });

  const claim = async (): Promise<void> => {
    try {
      calendar = await api.claimCalendar(calendar.id);
      options.onUpdated(calendar);
      message = { text: 'This calendar now belongs to your account.', kind: 'info' };
      toast('Calendar secured.');
      repaint();
    } catch (err) {
      toastError(err);
    }
  };

  const withPasskey = async (btn: HTMLButtonElement, run: () => Promise<User>): Promise<void> => {
    await withBusy(btn, async () => {
      try {
        user = await run();
        options.onSignedIn(user);
        await claim();
      } catch (err) {
        message = { text: passkeyErrorMessage(err), kind: 'alert' };
        repaint();
      }
    });
  };

  function unclaimedBody(): Child[] {
    // The passkey is registered under the person's name, not the calendar's —
    // it is what their authenticator will show them next time they pick a key,
    // and "Family and friends" is not who they are.
    const name = textField({
      label: 'Your name',
      name: 'display-name',
      value: displayName,
      placeholder: 'Ola Nilsson',
      maxlength: '60',
      autocomplete: 'name',
      hint: 'What your device will show when you use this passkey.',
    });
    name.input.addEventListener('input', () => {
      displayName = name.input.value;
      name.setError(null);
    });

    const register = button(
      'Create a passkey',
      () => {
        const value = name.input.value.trim();
        if (!value) {
          name.setError('Give a name to register the passkey under.');
          name.input.focus();
          return;
        }
        displayName = value;
        void withPasskey(register, () => registerPasskey(value));
      },
      { class: 'btn--primary' },
    );
    const signIn = button('Use an existing passkey', () => void withPasskey(signIn, () => signInWithPasskey()), {
      class: 'btn--small btn--quiet',
    });

    return [
      el('p', {
        class: 'muted',
        text: 'Right now this calendar is held by a link. Whoever has the link can edit it, and if you lose the link there is no way back in.',
      }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'Attach it to you' }),
        el('p', {
          class: 'small muted',
          text: 'A passkey is your fingerprint, face or device PIN. It stays on your device; there is no password to remember.',
        }),
        browserSupportsWebAuthn()
          ? el('div', { class: 'stack' }, name.root, el('div', { class: 'row' }, register, signIn))
          : el('div', {
              class: 'notice',
              text: githubEnabled()
                ? 'This browser cannot make passkeys. Use GitHub instead.'
                : 'This browser cannot make passkeys, so this calendar can only be held by its link here.',
            }),
        githubEnabled()
          ? el(
              'div',
              { class: 'small muted' },
              el('span', { text: 'Or ' }),
              el('a', { href: githubSignInUrl(`/circles/${calendar.id}`), rel: 'external', text: 'sign in with GitHub', onClick: rememberGithubSignIn }),
              el('span', { text: ' and come back here to finish.' }),
            )
          : null,
        user
          ? el(
              'div',
              { class: 'row' },
              el('span', { class: 'small muted', text: `Signed in as ${user.name}.` }),
              button('Attach this calendar', () => void claim(), { class: 'btn--small btn--primary' }),
            )
          : null,
      ),
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'Your owner link' }),
        el('p', { class: 'small muted', text: 'Keep this somewhere safe. It is the only way back into an unclaimed calendar.' }),
        ownerLinkRow(calendar),
      ),
    ];
  }

  /** The link came back changed from one of its controls (ui/owner-link.ts). */
  const linkUpdated = (updated: Calendar): void => {
    calendar = updated;
    options.onUpdated(calendar);
    repaint();
  };

  function claimedBody(): Child[] {
    return [
      el('p', { class: 'muted', text: `This calendar belongs to your account${user ? `, ${user.name}` : ''}. Signing in gets you back to it on any device.` }),
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'The owner link' }),
        el('p', {
          class: 'small muted',
          text: calendar.ownerTokenEnabled
            ? 'The link still works as a second key — handy on a device you have not signed in on, risky if it leaks.'
            : 'The link is switched off. Only your account can edit this calendar.',
        }),
        calendar.ownerTokenEnabled ? ownerLinkRow(calendar) : null,
        ...ownerLinkActions({ calendar, onUpdated: linkUpdated }),
      ),
    ];
  }

  /**
   * The way out of the product.
   *
   * It lives at the bottom of Keys because that is where the rest of "who can
   * open this and how" already is, and because deleting the ring is the last
   * key question there is. Until it existed a calendar could only be abandoned,
   * which also meant an account could never be closed: `DELETE /api/me` refuses
   * with `OWNS_CALENDARS` while anything is still attached.
   */
  function dangerSection(): Child[] {
    if (!options.onDeleted) return [];
    const remove = button('Delete this ring', () => confirmDelete(), {
      class: 'btn--small btn--danger',
      'data-role': 'delete-calendar',
    });
    return [
      el('hr', { class: 'rule' }),
      el(
        'section',
        { class: 'stack' },
        el('h3', { text: 'Delete this ring' }),
        el('p', {
          class: 'small muted',
          text: 'Everything on it goes at once, for everyone, and it cannot be undone.',
        }),
        el('div', { class: 'row' }, remove),
      ),
    ];
  }

  function confirmDelete(): void {
    // Not `confirmDialog`: what goes with a calendar is a list, and a list is
    // the point — most of it is links other people are holding.
    const handle = openDialog({
      title: `Delete “${calendar.name}”?`,
      description: `There is no way to get “${calendar.name}” back, and everyone you shared it with loses it at the same moment.`,
      body: [
        el(
          'ul',
          { class: 'small muted', style: 'padding-left:18px;margin:6px 0 0' },
          el('li', { text: 'Every birthday and note on the ring, and every photo on one of them.' }),
          el('li', { text: 'The share link: whoever you sent it to opens nothing.' }),
          el('li', { text: 'The calendar feed, which stops updating on the phones subscribed to it.' }),
          el('li', { text: 'The collect link, so nobody can add themselves any more.' }),
          el('li', { text: 'The owner link, which stops being a key to anything.' }),
        ),
      ],
    });
    const cancel = button('Keep it', () => handle.close());
    const confirm = button(
      'Delete this ring',
      () =>
        void withBusy(confirm, async () => {
          try {
            await api.deleteCalendar(calendar.id);
          } catch (err) {
            toastError(err, 'The ring was not deleted.');
            return;
          }
          // The token opens nothing now, and leaving it behind would keep the
          // calendar on the front page's list of rings this device holds keys
          // for, where it would 404.
          ownerTokens.remove(calendar.id);
          handle.close();
          panel.close();
          toast(`“${calendar.name}” is gone.`);
          options.onDeleted?.();
        }),
      { class: 'btn--danger' },
    );
    handle.foot.append(el('span', { class: 'grow' }), cancel, confirm);
    handle.root.appendChild(handle.foot);
  }

  function content(): Child[] {
    const banner = message
      ? el('div', { class: message.kind === 'alert' ? 'notice notice--alert' : 'notice', text: message.text })
      : null;
    return [
      banner,
      ...(calendar.claimed ? claimedBody() : unclaimedBody()),
      ...dangerSection(),
    ];
  }

  panel.open({ title: 'Keys and access', body: content() }, { key: 'security' });
}
